import { useState, useEffect, useRef } from "react";
import "./Team.css";
import teamData from "./teamData";

const TeamSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const sliderRef = useRef(null);
  const scrollTimeout = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Scroll logic
  useEffect(() => {
    const handleWheel = (e) => {
      if (isMobile || isDragging) return;

      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

      scrollTimeout.current = setTimeout(() => {
        if (e.deltaX > 0 && currentIndex < teamData.length - 1) {
          setCurrentIndex((prev) => prev + 1);
        } else if (e.deltaX < 0 && currentIndex > 0) {
          setCurrentIndex((prev) => prev - 1);
        }
      }, 50);
    };

    const slider = sliderRef.current;
    if (slider)
      slider.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      if (slider) slider.removeEventListener("wheel", handleWheel);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [currentIndex, isMobile, isDragging]);

  const handleDragStart = (e, x) => {
    if (e.target.closest(".linkedin")) return;

    if (isMobile) return;
    setIsDragging(true);
    setStartX(x);
    setDragOffset(0);
    setHasDragged(false);
  };

  const handleDragMove = (x) => {
    if (!isDragging || isMobile) return;
    const offset = x - startX;
    setDragOffset(offset);

    if (Math.abs(offset) > 5) {
      setHasDragged(true);
    }
  };

  const handleDragEnd = () => {
    if (!isDragging || isMobile) return;
    setIsDragging(false);

    const threshold = 100;
    if (dragOffset > threshold && currentIndex > 0)
      setCurrentIndex(currentIndex - 1);
    else if (dragOffset < -threshold && currentIndex < teamData.length - 1)
      setCurrentIndex(currentIndex + 1);

    setDragOffset(0);
    setTimeout(() => setHasDragged(false), 100);
  };

  const handleLinkClick = (e) => {
    if (hasDragged) {
      e.preventDefault();
    }
  };

  const handleDotClick = (i) => {
    if (!isDragging) setCurrentIndex(i);
  };

  const getCardStyle = (i) => {
    const diff = i - currentIndex;
    const drag = isDragging ? dragOffset : 0;
    let x = 0,
      scale = 1,
      opacity = 1,
      z = 0,
      blur = 0;

    if (diff === -1) {
      x = -600 + drag;
      scale = 0.75;
      opacity = 0.4;
      z = 1;
      blur = 4;
    } else if (diff === 0) {
      x = drag;
      scale = 1;
      z = 10;
    } else if (diff === 1) {
      x = 600 + drag;
      scale = 0.75;
      opacity = 0.4;
      z = 1;
      blur = 4;
    } else {
      x = diff < 0 ? -1200 : 1200;
      scale = 0.75;
      opacity = 0;
      blur = 4;
    }

    return {
      transform: `translateX(${x}px) scale(${scale})`,
      opacity,
      zIndex: z,
      filter: `blur(${blur}px)`,
      transition: isDragging ? "none" : "all 0.5s ease",
    };
  };

  return (
    <section className="teamSection">
      <div className="teamContainer">
        <div className="teamHead">
          <h2 className="headTitle">Meet the Minds,<br />
            <span className="spanColor">Behind the Magic.</span>
          </h2>
          <p className="teamPara">
            The passionate team turning ideas into digital experiences.
          </p>
        </div>

        {isMobile ? (
          <div className="mobileWrapper">
            {teamData.map((m, i) => (
              <div key={i} className="mobileCard">
                {/* mobile */}
                <div className="mobileImgWrapper">
                  <img src={m.image} alt={m.name} className="currImg" />
                </div>
                {/* end */}

                <div className="cardContent">
                  <h3 className="memberInfo">
                    <span className="spanColor">{m.name}</span>
                    <span className="role_text">({m.role})</span>
                  </h3>
                  <p className="memDesc">{m.description}</p>
                  <a
                    href={m.social || m.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="linkedin"
                    onClick={handleLinkClick}
                  >
                    Linked <span className="spanColor">in</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="carouselContainer">
              <div
                ref={sliderRef}
                className="slideWrapper"
                onMouseDown={(e) => handleDragStart(e, e.clientX)}
                onMouseMove={(e) => handleDragMove(e.clientX)}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                onTouchStart={(e) => handleDragStart(e, e.touches[0].clientX)}
                onTouchMove={(e) => handleDragMove(e.touches[0].clientX)}
                onTouchEnd={handleDragEnd}
              >
                <div className="cardContainer">
                  {teamData.map((m, i) => {
                    const show = Math.abs(i - currentIndex) <= 1;
                    if (!show) return null;

                    return (
                      <div key={i} className="currCard" style={getCardStyle(i)}>

                        <div className="cardImageWrapper">
                          <img
                            src={m.image}
                            alt={m.name}
                            className="cardImage"
                            draggable="false"
                          />
                        </div>

                        <div className="cardContent">
                          <h3 className="memberInfo">
                            <span className="spanColor">{m.name}</span>
                            <span className="role_text">({m.role})</span>
                          </h3>
                          <p className="memDesc">{m.description}</p>
                          <a
                            href={m.social}
                            className="linkedin"
                            target="_blank"
                            rel="noopener noreferrer"
                            onMouseDown={(e) => e.stopPropagation()}
                            onTouchStart={(e) => e.stopPropagation()}
                            onClick={handleLinkClick}
                          >
                            Linked <span className="spanColor">in</span>
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="bottomDots">
              <div className="dotWrapper">
                {teamData.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => handleDotClick(i)}
                    className={`dotBase ${i === currentIndex ? "dotActive" : ""
                      }`}
                    disabled={isDragging}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default TeamSection;
